/**
 * Pure view helpers for the vehicle diagnostic chat UI.
 * Implements the programmatic acceptance criteria in test/specs/vehicle-chat-ui.md.
 * No React/DOM imports — types only.
 */

/** The un-trimmed message cap, mirroring the server's @Length(1, 2000). */
export const MAX_MESSAGE_LENGTH = 2000;

export interface QuotaView {
  remaining: number;
  limit: number;
}

export interface CitableMessage {
  codesCited: string[];
  recallsCited: string[];
}

/** Meter label, e.g. "12 of 50 questions left today"; negative remaining clamps to 0, singular at exactly 1. */
export function formatRemaining(quota: QuotaView): string {
  const remaining = Math.max(0, quota.remaining);
  const noun = remaining === 1 ? 'question' : 'questions';
  return `${remaining} of ${quota.limit} ${noun} left today`;
}

/** Trim, drop blanks, then transform each surviving entry. */
function cleanEntries(entries: string[], transform: (s: string) => string): string[] {
  return entries
    .filter((e) => typeof e === 'string' && e.trim() !== '')
    .map((e) => transform(e.trim()));
}

/** Ordered, de-duped chip labels: OBD codes (upper-cased) first, then recall numbers. */
export function citationChips(message: CitableMessage): string[] {
  const codes = cleanEntries(message.codesCited ?? [], (s) => s.toUpperCase());
  const recalls = cleanEntries(message.recallsCited ?? [], (s) => s);
  // A Set preserves first-insertion order: codes stay first, duplicates drop.
  return [...new Set([...codes, ...recalls])];
}

/** True only when trimmed input is non-empty, raw length ≤ 2000, quota remains, and no send is in flight. */
export function canSend(input: string, quota: QuotaView, inFlight: boolean): boolean {
  if (inFlight) return false;
  if (quota.remaining <= 0) return false;
  if (input.trim().length === 0) return false;
  return input.length <= MAX_MESSAGE_LENGTH;
}
